// @ts-strict-ignore
import React, { useCallback, useEffect, useMemo } from 'react'
import { defaultTeleprompterText } from 'components/FreelancerProfile/components/fields/VideoField/utils/defaultTeleprompterText'
import { Button, List, ListItem, ListItemIcon, ListItemText, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { CheckCircle, SwitchVideo } from '@mui/icons-material'
import { goToLogin } from 'services/router'
import { useRouter } from 'next/router'
import { useLazyLoadQuery } from 'react-relay'
import { graphql } from 'relay-runtime'
import { RecordVideoButton_Query } from '__generated__/RecordVideoButton_Query.graphql'
import { getErrorText } from 'services/error'
import Condition from 'components/Condition'
import LoadingIcon from 'components/LoadingIcon'
import Suspense from 'components/Suspense'
import { useFragment } from 'react-relay/hooks'
import { RecordVideoButton_Question$key } from '__generated__/RecordVideoButton_Question.graphql'
import { IUploadedVideo, useVideoRecorder } from 'hooks/video'
import { isGuest } from 'services/user'
import { RecordVideoButton_Video$key } from '__generated__/RecordVideoButton_Video.graphql'
import { makeStyles } from 'tss-react/mui'
import { preloadableLazy } from 'services/react'

const VideoRecorder = preloadableLazy(() => (
  import(/* webpackChunkName: "VideoRecorder" */'components/VideoRecorder')
))

interface IRecordVideoButtonProps {
  questionFragmentRef?: RecordVideoButton_Question$key
  enableCompletionBadge?: boolean
  maxDuration?: number
  onVideoReady: (video: IUploadedVideo) => void
  videoFragmentRef?: RecordVideoButton_Video$key
}

const useStyles = makeStyles()(theme => ({
  label: {
    display: 'inline-block',
    verticalAlign: 'middle',
    paddingLeft: theme.spacing(2),
    color: 'rgba(0, 0, 0, 0.6)',
  },
  divider: {
    margin: `${theme.spacing(4)} 0 !important`,
  },
}))

function RecordVideoButton({
  videoFragmentRef,
  enableCompletionBadge = false,
  onVideoReady,
  maxDuration,
  questionFragmentRef,
}: IRecordVideoButtonProps) {
  const { classes } = useStyles()
  const theme = useTheme()
  const router = useRouter()

  const question = useFragment(graphql`
    fragment RecordVideoButton_Question on Question {
      id
      rawId
      title
      videoAnswer {
        video {
          ...RecordVideoButton_Video
        }
      }
    }
  `, questionFragmentRef)

  const data = useLazyLoadQuery<RecordVideoButton_Query>(graphql`
    query RecordVideoButton_Query {
      currentUser {
        video {
          ...RecordVideoButton_Video
        }

        ...user_isGuest
      }
      featuredFreelancers {
        video {
          ...SampleVideosButton_SampleVideos
        }
      }
      configuration {
        videoMinDuration
      }
    }
  `, {}, {
    fetchPolicy: 'store-and-network',
  })

  const video = useFragment(graphql`
    fragment RecordVideoButton_Video on Video {
      url
    }
  `, (videoFragmentRef || question?.videoAnswer?.video || data?.currentUser?.video) as RecordVideoButton_Video$key)

  const sampleVideos = useMemo(() => (
    data?.featuredFreelancers?.map(f => f.video)
  ), [data])

  const videoUrl = useMemo(() => video?.url, [video])

  const { videoRecorderProps, isUploading, isVideoRecorderOpen, openVideoRecorder, error } = useVideoRecorder({
    onUploadCompleted: (newVideoData) => {
      if (onVideoReady) onVideoReady(newVideoData)
    },
    videoParams: {
      questionId: question?.id,
    },
  })

  const handleOpen = useCallback(() => {
    if (isGuest(data?.currentUser)) {
      goToLogin(router)
    } else {
      openVideoRecorder()
    }
  }, [data, openVideoRecorder])

  useEffect(() => {
    VideoRecorder.preload()
  }, [])

  const icon = isUploading ? (<LoadingIcon style={{ marginRight: theme.spacing(2) }} />) : (<SwitchVideo />)
  const dataCy = question ? `answer-question-${question.rawId}` : 'record-video-introduction'

  return (
    <React.Fragment>
      {videoUrl && enableCompletionBadge && (
        <List disablePadding style={{ marginBottom: theme.spacing(2), marginTop: theme.spacing(-2) }}>
          <ListItem data-cy="video-recorded-badge">
            <ListItemIcon>
              <CheckCircle />
            </ListItemIcon>

            <ListItemText
              primary={question ? 'Video Answer Recorded' : 'Video Introduction Recorded'}
            />
          </ListItem>
        </List>
      )}
      {videoUrl ? (
        <Button color="primary" variant="contained" disabled={isUploading} onClick={handleOpen} data-cy={dataCy}>
          {icon} View Or Replace Video {question ? 'Answer' : 'Introduction'}
        </Button>
      ) : (
        <Button color="primary" variant="contained" disabled={isUploading} onClick={handleOpen} data-cy={dataCy}>
          {icon} Record your Video {question ? 'Answer' : 'Introduction'}
        </Button>
      )}

      {(isUploading || error) && (
        <Typography variant="body1" className={classes.label} data-cy="video-uploading-message">
          {isUploading && 'Uploading new video...'}
          {error && getErrorText(error)}
        </Typography>
      )}

      <Condition condition={isVideoRecorderOpen}>
        <Suspense noSsr>
          <VideoRecorder
            {...videoRecorderProps}
            title={question ? question.title : 'Your Video Introduction'}
            sampleVideosFragmentsRefs={sampleVideos}
            defaultTeleprompterText={question ? null : defaultTeleprompterText}
            defaultVideoURL={videoUrl}
            maxDuration={maxDuration}
            minDuration={data?.configuration?.videoMinDuration}
          />
        </Suspense>
      </Condition>
    </React.Fragment>
  )
}

export default React.memo(RecordVideoButton)
